import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '@/utils/api-request';
import { Search, MapPin, X, Check, ChevronDown, Loader2 } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';

interface TargetLocation {
    id?: number;
    city_id: number;
    area_id: number;
    city_name: string;
    area_name: string;
}

interface City {
    cityId: number;
    cityName: string;
    country_id?: number;
}

interface Area {
    areaId: number;
    areaName: string;
    areaSlug: string;
    city_id_fk: number;
}

const MAX_LOCATIONS = 20;

const TargetLocations: React.FC = () => {
    const { id } = useParams<{ id: string }>();

    const [targetLocations, setTargetLocations] = useState<TargetLocation[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    // City state
    const [allCities, setAllCities] = useState<City[]>([]);
    const [cities, setCities] = useState<City[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<number>(1);
    const [selectedCity, setSelectedCity] = useState<City | null>(null);
    const [citySearch, setCitySearch] = useState('');
    const [isCityOpen, setIsCityOpen] = useState(false);
    const cityRef = useRef<HTMLDivElement>(null);

    // Area state
    const [areas, setAreas] = useState<Area[]>([]);
    const [loadingAreas, setLoadingAreas] = useState(false);
    const [areaSearch, setAreaSearch] = useState('');
    const [isAreaOpen, setIsAreaOpen] = useState(false);
    const areaRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchTargetLocations();
        fetchCities();
    }, [id]);

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (cityRef.current && !cityRef.current.contains(e.target as Node)) setIsCityOpen(false);
            if (areaRef.current && !areaRef.current.contains(e.target as Node)) setIsAreaOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchTargetLocations = async () => {
        try {
            setLoading(true);
            const response = await apiRequest(`/schools/${id}/target-locations`);
            if (response.success) {
                setTargetLocations(response.data.locations || []);
            }
        } catch (err) {
            console.error('Error fetching target locations:', err);
            showToast('Failed to load target locations', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchCities = async () => {
        try {
            const response = await apiRequest('/cities');
            if (response.success && response.data?.cities) {
                setAllCities(response.data.cities);
                setCities(response.data.cities.filter((c: City) => (c.country_id ?? 1) === selectedCountry));
            }
        } catch (err) {
            console.error('Error fetching cities:', err);
        }
    };

    useEffect(() => {
        if (allCities.length === 0) return;
        setCities(allCities.filter(c => (c.country_id ?? 1) === selectedCountry));
        setSelectedCity(null);
        setCitySearch('');
        setAreas([]);
        setAreaSearch('');
    }, [selectedCountry, allCities]);

    const fetchAreas = async (cityId: number) => {
        try {
            setLoadingAreas(true);
            setAreas([]);
            const response = await apiRequest(`/schools/areas/${cityId}`);
            if (response.success && response.data?.areas) {
                setAreas(response.data.areas);
            }
        } catch (err) {
            console.error('Error fetching areas:', err);
        } finally {
            setLoadingAreas(false);
        }
    };

    const handleCitySelect = (city: City) => {
        setSelectedCity(city);
        setCitySearch(city.cityName);
        setIsCityOpen(false);
        setAreaSearch('');
        setAreas([]);
        fetchAreas(city.cityId);
    };

    const handleAreaAdd = (area: Area) => {
        if (!selectedCity) return;

        if (targetLocations.length >= MAX_LOCATIONS) {
            showToast(`Maximum ${MAX_LOCATIONS} locations allowed`, 'error');
            return;
        }

        if (targetLocations.some(loc => loc.area_id === area.areaId)) {
            showToast('This location is already added', 'error');
            return;
        }

        setTargetLocations(prev => [...prev, {
            city_id: selectedCity.cityId,
            area_id: area.areaId,
            city_name: selectedCity.cityName,
            area_name: area.areaName,
        }]);
        setDirty(true);
        setAreaSearch('');
        setIsAreaOpen(false);
    };

    const handleRemove = (areaId: number) => {
        setTargetLocations(prev => prev.filter(loc => loc.area_id !== areaId));
        setDirty(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const response = await apiRequest(`/schools/${id}/target-locations`, {
                method: 'POST',
                body: JSON.stringify({
                    locations: targetLocations.map(loc => ({
                        city_id: loc.city_id,
                        area_id: loc.area_id,
                    })),
                }),
            });
            if (response.success) {
                showToast('Target locations saved successfully', 'success');
                setDirty(false);
                fetchTargetLocations();
            } else {
                showToast(response.message || 'Failed to save locations', 'error');
            }
        } catch (err: any) {
            showToast(err.message || 'Failed to save locations', 'error');
        } finally {
            setSaving(false);
        }
    };

    const filteredCities = cities.filter(c =>
        c.cityName.toLowerCase().includes(citySearch.toLowerCase())
    );

    const filteredAreas = areas.filter(a =>
        a.areaName.toLowerCase().includes(areaSearch.toLowerCase())
    );

    return (
        <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-brandColor rounded-lg text-white">
                            <MapPin className="h-5 w-5" />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Target Locations</h1>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Select locations for Ads targeting (max {MAX_LOCATIONS})</p>
                        </div>
                    </div>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={saving || !dirty}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="flex-1 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 text-brandColor animate-spin" />
                </div>
            ) : (
                <div className="flex-1 overflow-auto p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left Column - Add Location */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                            <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Add Location</h2>

                            {/* City Dropdown */}
                            <div className="mb-4">
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">City</label>
                                <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden mb-2">
                                    {[{ label: '🇮🇳 India', value: 1 }, { label: '🇦🇪 UAE', value: 2 }].map(opt => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setSelectedCountry(opt.value)}
                                            className={`flex-1 py-1.5 text-sm font-medium transition-colors ${selectedCountry === opt.value ? 'bg-brandColor text-white' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="relative" ref={cityRef}>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                        <input
                                            type="text"
                                            value={citySearch}
                                            onChange={(e) => {
                                                setCitySearch(e.target.value);
                                                setIsCityOpen(true);
                                                if (selectedCity) {
                                                    setSelectedCity(null);
                                                    setAreas([]);
                                                    setAreaSearch('');
                                                }
                                            }}
                                            onFocus={() => setIsCityOpen(true)}
                                            placeholder="Search city..."
                                            className="w-full pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                                        />
                                        <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 transition-transform ${isCityOpen ? 'rotate-180' : ''}`} />
                                    </div>
                                    {isCityOpen && (
                                        <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                            {filteredCities.length === 0 ? (
                                                <div className="px-3 py-2 text-sm text-gray-500">No cities found</div>
                                            ) : (
                                                filteredCities.map(city => (
                                                    <button
                                                        key={city.cityId}
                                                        type="button"
                                                        onClick={() => handleCitySelect(city)}
                                                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 ${selectedCity?.cityId === city.cityId ? 'bg-brandColor/10 text-brandColor font-medium' : 'text-gray-700 dark:text-gray-200'}`}
                                                    >
                                                        {city.cityName}
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Area List */}
                            <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Area</label>
                                <div className="relative" ref={areaRef}>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                        <input
                                            type="text"
                                            value={areaSearch}
                                            onChange={(e) => {
                                                setAreaSearch(e.target.value);
                                                setIsAreaOpen(true);
                                            }}
                                            onFocus={() => selectedCity && setIsAreaOpen(true)}
                                            placeholder={loadingAreas ? 'Loading areas...' : !selectedCity ? 'Select a city first' : 'Search area...'}
                                            disabled={!selectedCity || loadingAreas}
                                            className="w-full pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white disabled:opacity-50"
                                        />
                                        <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 transition-transform ${isAreaOpen ? 'rotate-180' : ''}`} />
                                    </div>
                                    {isAreaOpen && selectedCity && !loadingAreas && (
                                        <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                            {filteredAreas.length === 0 ? (
                                                <div className="px-3 py-2 text-sm text-gray-500">No areas found</div>
                                            ) : (
                                                filteredAreas.map(area => {
                                                    const alreadyAdded = targetLocations.some(loc => loc.area_id === area.areaId);
                                                    return (
                                                        <button
                                                            key={area.areaId}
                                                            type="button"
                                                            onClick={() => !alreadyAdded && handleAreaAdd(area)}
                                                            disabled={alreadyAdded}
                                                            className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between ${alreadyAdded ? 'bg-gray-50 dark:bg-gray-800 text-gray-400 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200'}`}
                                                        >
                                                            {area.areaName}
                                                            {alreadyAdded && <Check className="w-4 h-4 text-green-500" />}
                                                        </button>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {targetLocations.length >= MAX_LOCATIONS && (
                                <p className="text-sm text-red-500 mt-3">Maximum {MAX_LOCATIONS} locations reached. Remove some to add more.</p>
                            )}
                        </div>

                        {/* Right Column - Selected Locations */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                            <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <Check size={16} className="text-green-600" />
                                Selected Locations ({targetLocations.length}/{MAX_LOCATIONS})
                            </h2>

                            <div className="space-y-2 max-h-[500px] overflow-y-auto">
                                {targetLocations.length === 0 ? (
                                    <div className="text-center py-8">
                                        <MapPin className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                                        <p className="text-sm text-gray-500 dark:text-gray-400">No locations selected</p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Select a city and area to add locations</p>
                                    </div>
                                ) : (
                                    targetLocations.map((loc) => (
                                        <div
                                            key={`${loc.city_id}-${loc.area_id}`}
                                            className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{loc.area_name}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">{loc.city_name}</p>
                                            </div>
                                            <button
                                                onClick={() => handleRemove(loc.area_id)}
                                                className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded ml-2"
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TargetLocations;
